'use client'

import Script from 'next/script'
import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect, useRef } from 'react'
import { trackPageView } from '@/lib/analytics'

export function GoogleAnalytics() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID

  // Track page views on SPA route change.
  // The initial landing page view is sent natively by gtag (`send_page_view: true`
  // below), which is timing-independent and fires even for early-bounce visitors.
  // This effect must therefore SKIP its first run, or the landing page would be
  // counted twice (native + manual). It only fires for subsequent navigations.
  // Hook must run unconditionally (rules-of-hooks); gate the work on measurementId inside.
  const isFirstRun = useRef(true)
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false
      return
    }
    if (pathname && measurementId) {
      const url = pathname + (searchParams?.toString() ? `?${searchParams.toString()}` : '')
      trackPageView(url)
    }
  }, [pathname, searchParams, measurementId])

  if (!measurementId) {
    return null
  }

  return (
    <>
      {/* Google Analytics gtag.js.
          `afterInteractive` (not `lazyOnload`) so gtag/dataLayer exist right after
          hydration — before most visitors bounce. `lazyOnload` waits for full idle,
          which dropped the landing page view (and thus the session) for early-leave
          mobile SEO traffic. */}
      <Script
        src={`/sg/js?id=${measurementId}`}
        strategy="afterInteractive"
      />
      <Script id="google-analytics" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}

          gtag('consent', 'default', { 'analytics_storage': 'granted' });

          gtag('js', new Date());

          gtag('config', '${measurementId}', {
            page_path: window.location.pathname,
            // Fire the landing page view natively. This queues into dataLayer and
            // sends the moment gtag.js drains the queue — independent of React effect
            // timing, so it survives early-bounce visits. SPA navigations are still
            // tracked manually (GoogleAnalytics effect skips its first run to avoid
            // double-counting this initial view).
            transport_url: window.location.origin + '/sg',
            send_page_view: true
          });
        `}
      </Script>
    </>
  )
}
